import React, { createContext, useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { trackPromise } from "react-promise-tracker";
import { keycloakUser } from "../helper/context/actions";
import { SmarkReducer, initialState } from "../helper/context/reducer";
import { customFetch } from "../helper/api-helper/apiHerlpers";
import { format } from "react-string-format";
import apiUrls from "../constants/apiUrls";
import Loader from "react-loader-spinner";

const KeycloackContext = createContext();
const SmarkContext = createContext();
const SmarkDispatchContext = createContext();

export const useKeycloakState = () => React.useContext(KeycloackContext);
export const useSmarkState = () => React.useContext(SmarkContext);
export const useSmarkDispatch = () => React.useContext(SmarkDispatchContext);

const loadKeycloak = () => import("keycloak-js");

const KeycloackContextProvider = (props) => {
  const [kc, setKc] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [kcInitialized, setKcInitialized] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [kcConfig, setKcConfig] = useState(null);

  const [user, dispatch] = useReducer(SmarkReducer, initialState);

  
  useEffect(() => {
    const cachedSite = sessionStorage.getItem("siteConfig");
    if (cachedSite) {
      const { applicationId, siteId } = JSON.parse(cachedSite);
      setApplicationId(applicationId);
      setSiteId(siteId);
      return;
    }

    const bootstrap = async () => {
      try {
        const domain = window.location.hostname;
        const res = await customFetch(format(apiUrls.GetSiteId, domain));

        if (!res?.applicationId || !res?.siteId) {
          toast.error("Invalid domain configuration");
          return;
        }

        setApplicationId(res.applicationId);
        setSiteId(res.siteId);

        localStorage.setItem("applicationid", res.applicationId);
        localStorage.setItem("siteid", res.siteId);

        sessionStorage.setItem(
          "siteConfig",
          JSON.stringify({
            applicationId: res.applicationId,
            siteId: res.siteId,
          })
        );
      } catch (err) {
        console.error("Bootstrap failed", err);
        toast.error("Failed to initialize application");
      }
    };

    bootstrap();
  }, []);

  
  useEffect(() => {
    if (!applicationId || !siteId) return;

    const cachedConfig = sessionStorage.getItem("kcConfig");
    if (cachedConfig) {
      setKcConfig(JSON.parse(cachedConfig));
      return;
    }

    const loadKeycloakConfig = async () => {
      try {
        const [realmRes, clientRes, authRes, logoRes, faviconRes] = await Promise.all([
          customFetch(format(apiUrls.GetRealm, siteId)),
          customFetch(format(apiUrls.GetClientId, siteId)),
          customFetch(format(apiUrls.GetAuthUrl, applicationId)),
          customFetch(format(apiUrls.GetLogoUrl, siteId)),
          customFetch(format(apiUrls.GetFaviconUrl, siteId)),
        ]);

        const config = {
          realm: realmRes?.value,
          clientId: clientRes?.value,
          authUrl: authRes?.applicationValue.endsWith("/")
            ? authRes.applicationValue
            : authRes.applicationValue + "/",
          logoUrl: logoRes?.value,
          faviconUrl: faviconRes?.value,
        };

        if (!config.realm || !config.clientId || !config.authUrl) {
          toast.error("Invalid Keycloak configuration");
          return;
        }

        setKcConfig(config);
        sessionStorage.setItem("kcConfig", JSON.stringify(config));

        if (config.faviconUrl) {
          sessionStorage.setItem("dynamicFavicon", config.faviconUrl);
        }
      } catch (err) {
        console.error("Keycloak config error", err);
        toast.error("Failed to load Keycloak config");
      }
    };

    loadKeycloakConfig();
  }, [applicationId, siteId]);

  
  useEffect(() => {
    if (!kcConfig) return;

    const initKeycloak = async () => {
      try {
        const { default: Keycloak } = await loadKeycloak();

        const keycloak = new Keycloak({
          realm: kcConfig.realm,
          url: kcConfig.authUrl,
          clientId: kcConfig.clientId,
        });

        const isAuth = await keycloak.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
          checkLoginIframe: false,
        });

        setKc(keycloak);
        setAuthenticated(isAuth);
        setKcInitialized(true);

        if (!isAuth) {
          redirectToLogin(keycloak, kcConfig);
          return;
        }

      
        const url = new URL(window.location.href);
        ["code", "session_state"].forEach((p) => url.searchParams.delete(p));
        window.history.replaceState({}, "", url.pathname);

        
        trackPromise(
          keycloakUser(
            dispatch,
            applicationId,
            siteId,
            keycloak.tokenParsed.preferred_username,
            keycloak.token
          )
            .then(() => {
              setUserLoaded(true);

              
              if (
                window.location.pathname === "/" &&
                !sessionStorage.getItem("postLoginRedirect")
              ) {
                window.location.href = "/dashboard";
              }
            })
            .catch(() => setUserLoaded(true)) 
        );

        // Fallback: if userLoaded not set after 2s, mark as loaded to render dashboard
       // setTimeout(() => {
      //    setUserLoaded(true);
      //  }, 2000);
      } catch (err) {
        console.error("Keycloak init failed", err);
        setKcInitialized(true);
        setUserLoaded(true);
      }
    };

    initKeycloak();
  }, [kcConfig]);

  
  const redirectToLogin = (keycloakObj, config) => {
    const base = `${config.authUrl}realms/${config.realm}/protocol/openid-connect/auth`;

    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: "code",
      redirect_uri: `${window.location.origin}/dashboard`,
      scope: "openid profile email",
      "logo-url": config.logoUrl,
      "favicon-url": config.faviconUrl,
    });

    window.location.href = `${base}?${params.toString()}`;
  };

  
  const logout = () => {
    if (!kc) return;

    setKc(null);
    setAuthenticated(false);

    localStorage.clear();
    sessionStorage.clear();

    kc.logout({
      redirectUri: window.location.origin,
    });
  };

 
  useEffect(() => {
    if (kc?.token) {
      localStorage.setItem("keycloakToken", kc.token);
    }
  }, [kc]);

  
  if (!kcInitialized || (authenticated && !userLoaded)) {
    return (
          <div className="spinner">
            <Loader type="Oval" color="#191f39" height={70} width={70} />
          </div>
        );
  }

  return (
    <KeycloackContext.Provider value={{ kc, authenticated, kcInitialized, logout }}>
      <SmarkContext.Provider value={user}>
        <SmarkDispatchContext.Provider value={dispatch}>
          {props.children}
        </SmarkDispatchContext.Provider>
      </SmarkContext.Provider>
    </KeycloackContext.Provider>
  );
};

export default KeycloackContextProvider;
